import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner, Badge } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import debounce from 'lodash/debounce';
import CommonSalesInternationalFields from "./CommonSalesInternationalFields";

const SOPIConvert = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [status, setStatus] = useState("Open");

  // Form state
  const [formData, setFormData] = useState({
    customer: null,
    customerDetails: {
      contactperson: '',
      contactno: '',
      email: '',
      paymentterm: '',
      incoterms: ''
    },
    customerMultipleAddress: null,
    shipTo: null,
    billTo: null,
    branch: null,
    warehouse: null,
    tallyreferenceno: '',
    dispatchthrough: '',
    destination: '',
    reference: '',
    packinglistno: '',
    packinglistdate: '',
    buyerorderno: '',
    buyerorderdate: '',

    // New international fields
    kindattn: '',
    countryofdestination: '',
    countryoforigin: '',
    airsea: '',
    portofloading: '',
    portofdischarge: '',
    noofboxes: '',
    netweightofboxes: '',
    grossweightofboxes: '',
    lutbondno: '',
    typeofshippingbill: '',
    drawbackacno: '',
    chapterheading: '',
    tariffitem: '',
    description: '',
    drawbackrate: '',
    lut: '',
    lutbonddate: '',
    fromdate: '',
    todate: '',

    rows: [],
    exchangeRate: 1,
    exportchargescurrency: 0,
    exportpreperationcharges: '',
    selectedCurrency: " "

  });
  const [invoicedate, setinvoicedate] = useState('');
  const [invoiceno, setinvoiceno] = useState('');
  const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
  const [isEnquiryValid, setIsEnquiryValid] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const checkinvoiceno = async (number) => {
    if (!number) {
      setEnquiryValidationMsg('');
      setIsEnquiryValid(false);
      return;
    }

    if (!/^[A-Za-z0-9-]+$/.test(number)) {
      setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
      setIsEnquiryValid(false);
      return;
    }

    try {
      const response = await axios.get(`${BASE_URL}sales/check/${number}`, {
        headers: authHeader(),
      });

      if (response.data === true) {
        setEnquiryValidationMsg('❌ This sales number already exists.');
        setIsEnquiryValid(false);
      } else if (response.data === false) {
        setEnquiryValidationMsg('✅ This sales number is available.');
        setIsEnquiryValid(true);
      } else {
        setEnquiryValidationMsg('Unexpected response from server.');
        setIsEnquiryValid(false);
      }
    } catch (error) {
      console.error("Error checking enquiry number:", error);
      setEnquiryValidationMsg('❌ Error validating sales number.');
      setIsEnquiryValid(false);
    }
  };

  useEffect(() => {
    const debouncedCheck = debounce(() => {
      checkinvoiceno(invoiceno);
    }, 400);

    debouncedCheck();

    return () => debouncedCheck.cancel();
  }, [invoiceno]);

  const fetchSalesData = async () => {
    try {
      const response = await axios.get(`${BASE_URL}salesOrder/findById/${id}`, {
        headers: authHeader()
      });

      const data = response.data;
      setStatus(data.status || "Open");
      setinvoiceno(data.invoiceno);
      setinvoicedate(data.invoicedate?.split('T')[0] || '');

      setFormData({
        customer: data.customer ? {
          value: data.customer.id,
          label: data.customer.companyname
        } : null,
        customerDetails: {
          contactperson: data.contactperson || '',
          contactno: data.contactno || '',
          email: data.email || '',
          paymentterm: data.customer?.paymentterm || '',
          incoterms: data.customer?.incoterms || ''
        },
        customerMultipleAddress: data.customeraddress ? {
          value: data.customeraddress.id,
          label: data.customeraddress.customeraddress
        } : null,
        shipTo: data.shipto ? {
          value: data.shipto.id,
          label: data.shipto.customeraddress
        } : null,
        billTo: data.billto ? {
          value: data.billto.id,
          label: data.billto.customeraddress
        } : null,
        branch: data.branch ? {
          value: data.branch.id,
          label: data.branch.branchname
        } : null,
        warehouse: data.warehouse ? {
          value: data.warehouse.id,
          label: data.warehouse.warehousename
        } : null,
        tallyreferenceno: data.tallyreferenceno || '',
        dispatchthrough: data.dispatchthrough || '',
        destination: data.destination || '',
        reference: data.reference || '',
        packinglistno: data.packinglistno || '',
        packinglistdate: data.packinglistdate?.split('T')[0] || '',
        buyerorderno: data.buyerorderno || '',
        buyerorderdate: data.buyerorderdate?.split('T')[0] || '',

        // New international fields
        kindattn: data.kindattn || '',
        countryofdestination: data.countryofdestination || '',
        countryoforigin: data.countryoforigin || '',
        airsea: data.airsea || '',
        portofloading: data.portofloading || '',
        portofdischarge: data.portofdischarge || '',
        noofboxes: data.noofboxes || '',
        netweightofboxes: data.netweightofboxes || '',
        grossweightofboxes: data.grossweightofboxes || '',
        lutbondno: data.lutbondno || '',
        typeofshippingbill: data.typeofshippingbill || '',
        drawbackacno: data.drawbackacno || '',
        chapterheading: data.chapterheading || '',
        tariffitem: data.tariffitem || '',
        description: data.description || '',
        drawbackrate: data.drawbackrate || '',
        lut: data.lut || '',
        lutbonddate: data.lutbonddate?.split('T')[0] || '',
        fromdate: data.fromdate?.split('T')[0] || '',
        todate: data.todate?.split('T')[0] || '',

        rows: data.salesOrderItems?.map(item => ({
          value: item.product.id,
          label: item.product.description,
          description: item.product.description,
          productremarks: item.productremarks || '',
          quantity: item.quantity || 0,
          unitratecurrency: item.unitratecurrency || 0,
          totalcurrency: item.totalcurrency || 0,
          exchangerate: item.exchangerate || data.soexchangerate || 1,
          unitRateInr: item.unitrateinr || 0,
          totalInr: item.totalinr || 0,
          hsnsacno: item.hsnsacno || '',
          partno: item.partno || '',
          serialnumber: item.serialnumber || '',
          modelnumber: item.modelnumber || '',
          tagnumber: item.tagnumber || '',
          moc: item.moc || '',
          itemcode: item.itemcode || '',
          uom: item.uom || ''
        })) || [],
        exchangeRate: data.soexchangerate || 1,
        exportchargescurrency: data.exportpreperationchargescurrency || 0,
        exportpreperationcharges: data.exportpreperationcharges || '',
        selectedCurrency: data.salescurrency || "$ (USD)"
      });

    } catch (error) {
      console.error("Error fetching sales data", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch sales data. Please try again.",
      });
      navigate("/salesindex/1");
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchSalesData();
  }, [id]);

  const handleDataChange = (newData) => {
    setFormData(prev => ({
      ...prev,
      ...newData,
      rows: newData.rows || prev.rows
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setFormSubmitted(true);
    if (!isEnquiryValid) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please enter a valid sales number before submitting.',
      });
      return;
    }

    const {
      customer,
      customerDetails,
      customerMultipleAddress,
      shipTo,
      billTo,
      branch,
      warehouse,
      tallyreferenceno,
      dispatchthrough,
      destination,
      reference,
      packinglistno,
      packinglistdate,
      buyerorderno,
      buyerorderdate,

      // New international fields
      kindattn,
      countryofdestination,
      countryoforigin,
      airsea,
      portofloading,
      portofdischarge,
      noofboxes,
      netweightofboxes,
      grossweightofboxes,
      lutbondno,
      typeofshippingbill,
      drawbackacno,
      chapterheading,
      tariffitem,
      description,
      drawbackrate,
      lut,
      lutbonddate,
      fromdate,
      todate,

      rows,
      exchangeRate,
      exportchargescurrency,
      exportpreperationcharges,
      selectedCurrency
    } = formData;

    // Validation
    if (!customer) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please select a customer',
      });
      setIsLoading(false);
      return;
    }

    if (rows.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please add at least one product',
      });
      setIsLoading(false);
      return;
    }

    // Calculate totals
    const misccharges = exportchargescurrency * exchangeRate;
    const netTotalInr = rows.reduce((total, row) => total + (row.totalInr || 0), 0);
    const netTotalCurrency = rows.reduce((total, row) => total + (row.totalcurrency || 0), 0);
    const grandTotalInr = netTotalInr + misccharges;
    const grandTotalCurrency = netTotalCurrency + exportchargescurrency;

    // Prepare sales items
    const salesItems = rows.map(row => ({
      product: { id: row.value },
      productremarks: row.productremarks || '',
      quantity: row.quantity || 0,
      unitrateinr: row.unitRateInr || 0,
      totalinr: row.totalInr || 0,
      unitratecurrency: row.unitratecurrency || 0,
      totalcurrency: row.totalcurrency || 0,
      exchangerate: row.exchangerate || exchangeRate,
      itemcode: row.itemcode || '',
      hsnsacno: row.hsnsacno || '',
      serialnumber: row.serialnumber || '',
      modelnumber: row.modelnumber || '',
      tagnumber: row.tagnumber || '',
      moc: row.moc || '',
      partno: row.partno || '',
      uom: row.uom || '',
      currency: selectedCurrency,
    }));

    // Prepare main Sales data with all international fields
    const SalesData = {
      id: id,
      status: status,
      salestype: "Pump Spare",
      salescategory: "International",
      currencytype: selectedCurrency,
      tallyreferenceno,
      dispatchthrough,
      destination,
      reference,
      packinglistno,
      packinglistdate,
      buyerorderno,
      buyerorderdate,
      invoiceno,
      invoicedate,

      // New international fields
      kindattn,
      countryofdestination,
      countryoforigin,
      airsea,
      portofloading,
      portofdischarge,
      noofboxes,
      netweightofboxes,
      grossweightofboxes,
      lutbondno,
      typeofshippingbill,
      drawbackacno,
      chapterheading,
      tariffitem,
      description,
      drawbackrate,
      lut,
      lutbonddate,
      fromdate,
      todate,
      salesItems,
      customer: { id: customer.value },
      customeraddress: customerMultipleAddress ? { id: customerMultipleAddress.value } : null,
      shipto: shipTo ? { id: shipTo.value } : null,
      billto: billTo ? { id: billTo.value } : null,
      contactperson: customerDetails.contactperson,
      contactno: customerDetails.contactno,
      email: customerDetails.email,
      paymentterm: customerDetails.paymentterm,
      incoterms: customerDetails.incoterms,
      branch: branch ? { id: branch.value } : null,
      warehouse: warehouse ? { id: warehouse.value } : null,
      salescurrency: selectedCurrency,
      salesexchangerate: exchangeRate,
      grandtotalinr: grandTotalInr,
      grandtotalcurrency: grandTotalCurrency,
      exportpreperationchargescurrency: exportchargescurrency,
      exportpreperationcharges,
      nettotalinr: netTotalInr,
      nettotalcurrency: netTotalCurrency,
    };


    console.log("convert", SalesData)

    try {
      await axios.post(`${BASE_URL}sales/soToSales/${id}`, SalesData, {
        headers: authHeader()
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Sales Order converted to Sales successfully!',
      });
      navigate(`/salesindex/1`);
    } catch (error) {
      console.error("Error converting Sales Order to Sales:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to convert Sales Order to Sales. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="container text-center py-5">
        <Spinner animation="border" />
        <p>Loading sales order details...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <br />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Convert Sales Order to Sales</h4>
        <Badge bg={status === 'Open' ? 'warning' : status === 'Completed' ? 'success' : 'secondary'}>
          Status: {status}
        </Badge>
      </div>

      <Form>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Sales Invoice No.: *</Form.Label>
              <Form.Control
                type="text"
                value={invoiceno}
                onChange={e => {
                  setinvoiceno(e.target.value);
                  setFormSubmitted(false);
                }}
                isInvalid={
                  (formSubmitted && !isEnquiryValid) ||
                  enquiryValidationMsg.includes('exists') ||
                  enquiryValidationMsg.includes('Error') ||
                  enquiryValidationMsg.includes('allowed')
                }
                required
              />
              {enquiryValidationMsg && (
                <Form.Control.Feedback type={
                  enquiryValidationMsg.includes('exists') ||
                    enquiryValidationMsg.includes('Error') ||
                    enquiryValidationMsg.includes('allowed') ?
                    "invalid" : "valid"
                }>
                  {enquiryValidationMsg}
                </Form.Control.Feedback>
              )}
              {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                <Form.Control.Feedback type="invalid">
                  Please enter a valid sales number
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Sales Invoice Date</Form.Label>
              <Form.Control
                type="date"
                value={invoicedate}
                onChange={(e) => setinvoicedate(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <CommonSalesInternationalFields
          onDataChange={handleDataChange}
          initialData={formData}
          isUpdate={true}
        />

        <br />
        <div className="d-flex justify-content-end">
          <button
            className="btn btn-primary me-2"
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" />
                Converting...
              </>
            ) : 'CONVERT TO SALES'}
          </button>

          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => navigate(`/salesView/${id}`)}
            disabled={isLoading}
          >
            CANCEL
          </button>
        </div>
      </Form>
    </div>
  );
};

export default SOPIConvert;